import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import CircularProgress from "@mui/material/CircularProgress";
import React, { createContext, useCallback, useMemo, useState } from "react";
import useTheme from "@mui/styles/useTheme";
import { Theme } from "@mui/material/styles";

type DialogConfirmationProvidesProps = {
  children: React.ReactNode;
};

type DialogConfirmationOptions = {
  loading?: boolean;
  disabled?: boolean;
  onConfirmate?: () => void;
  onCancel?: () => void;
};

export type DialogConfirmationContextData = {
  open: boolean;
  show: (
    title: string,
    message: string,
    options?: DialogConfirmationOptions
  ) => void;
  close: () => void;
  loading: (status: boolean) => void;
};

export const DialogConfirmationContext =
  createContext<DialogConfirmationContextData>(
    {} as DialogConfirmationContextData
  );

export function DialogConfirmationProvider({
  children,
}: DialogConfirmationProvidesProps) {
  const theme: Theme = useTheme();
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [options, setOptions] = useState<DialogConfirmationOptions>();
  const [message, setMessage] = useState("");
  const [title, setTitle] = useState("");

  const show = useCallback(
    async (
      title: string,
      message: string,
      options?: DialogConfirmationOptions
    ) => {
      if (options) {
        setOptions(options);
      }
      setMessage(message);
      setTitle(title);
      setIsVisible(true);
    },
    []
  );

  const close = useCallback(() => {
    setIsVisible(false);
  }, []);

  const loading = useCallback((status: boolean) => {
    setOptions((options) => ({ ...options, loading: status }));
  }, []);

  const handleCancel = useCallback(() => {
    close();
    if (Boolean(options) && Boolean(options?.onCancel)) {
      //@ts-ignore
      options.onCancel();
    }
  }, [options, close]);

  const toastContextData = useMemo(() => {
    return {
      show,
      close,
      loading,
      open: isVisible,
    };
  }, [isVisible, close, show, loading]);

  return (
    <DialogConfirmationContext.Provider value={toastContextData}>
      {children}
      <Dialog
        open={isVisible}
        aria-labelledby="alert-dialog-confirmation"
        aria-describedby="alert-dialog-description-confirmation"
      >
        <DialogTitle id="alert-dialog-title">{title}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {message}
          </DialogContentText>
        </DialogContent>
        <DialogActions
          style={{ display: "flex", justifyContent: "space-between" }}
        >
          <Button
            disabled={options?.disabled || options?.loading}
            onClick={handleCancel}
          >
            Cancelar
          </Button>
          <Button
            disabled={options?.disabled || options?.loading}
            onClick={options?.onConfirmate}
            variant="contained"
            autoFocus
          >
            {options?.loading ? (
              <CircularProgress size={theme.typography.body1.fontSize} />
            ) : (
              "Confirmar"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </DialogConfirmationContext.Provider>
  );
}
